// The runtime half of post likes (see post-likes.ts), with no schema library, so a creator
// site's script stays small.

/** One post's counts for a social card, and whether the viewer likes it. */
export type PostEngagementCounts = { likes: number; liked: boolean; comments: number };

/**
 * What a feed's social cards learn after the page loads, from a private, uncached answer: a
 * shared copy of the page never carries counts or anyone's heart. Counts are discovery data, as
 * a title is, so a locked post shows them too; its comments stay with those who may read it.
 */
export type PostEngagement = {
  signedIn: boolean;
  /** The site offers gift memberships, so a card may link to /join/gift. */
  gifts: boolean;
  posts: Record<string, PostEngagementCounts>;
};

export type PostLike = { likes: number; liked: boolean };

/** Who liked a post, for its creator: the newest first, and how many in all. */
export type PostLikers = {
  count: number;
  people: { id: string; name: string; likedAt: string }[];
};

const compact = new Intl.NumberFormat("en-US", { notation: "compact" });

/** A card's count: 1.2K past a thousand, and zero as the icon alone. */
export function postCountLabel(count: number) {
  return count > 0 ? compact.format(count) : "";
}

/** The most posts one answer covers: a feed shows up to 20. */
export const postEngagementLimit = 20;

/** The post IDs a feed asks about: distinct, well formed and no more than a feed holds. */
export function postEngagementIds(value: string | null | undefined) {
  return [
    ...new Set(
      (value ?? "")
        .split(",")
        .map((id) => id.trim())
        .filter((id) => /^[A-Za-z0-9_-]{1,64}$/.test(id)),
    ),
  ].slice(0, postEngagementLimit);
}
